import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Bell, Moon } from "lucide-react";
import { PushOptInButton } from "@/components/site/PushOptInButton";
import { NewsletterPreferences } from "@/components/site/NewsletterPreferences";
import {
  CATEGORY_LABELS,
  DEFAULT_PREFS,
  getNotificationPrefs,
  setNotificationPrefs,
  type NotificationCategory,
  type NotificationPrefs,
} from "@/lib/notifications.functions";

export const Route = createFileRoute("/_authenticated/settings/notifications")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Notifications · KAfrequency" },
      { name: "description", content: "Choose which transmissions reach you." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NotificationSettings,
});

function NotificationSettings() {
  const load = useServerFn(getNotificationPrefs);
  const save = useServerFn(setNotificationPrefs);
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    load()
      .then((p) => setPrefs(p))
      .finally(() => setLoading(false));
  }, [load]);

  const commit = async (next: NotificationPrefs) => {
    setPrefs(next);
    try {
      await save({ data: next });
      setSavedAt(Date.now());
    } catch (err) {
      console.error("[notifications] save failed", err);
    }
  };

  const setCategory = (cat: NotificationCategory, value: boolean) => {
    commit({ ...prefs, categories: { ...prefs.categories, [cat]: value } });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Settings</p>
        <h1 className="mt-2 font-serif text-3xl text-gold-luminous sm:text-4xl">Notifications</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Choose which transmissions reach you. Master switch is off by default until you enable push in your device.
        </p>
      </header>

      <section className="mb-6 rounded-2xl border hairline bg-charcoal/50 p-4">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-obsidian">
            <Bell size={16} className="text-gold" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Enable on this device</p>
            <p className="text-xs text-muted-foreground">
              Grant browser permission and register a push subscription.
            </p>
          </div>
          <PushOptInButton compact />
        </div>
      </section>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading preferences…</p>
      ) : (
        <>
          <Toggle
            label="Master switch"
            description="Silence every category at once."
            checked={prefs.master}
            onChange={(v) => commit({ ...prefs, master: v })}
          />

          <section className="mt-8">
            <h2 className="mb-3 text-xs uppercase tracking-[0.24em] text-muted-foreground">Categories</h2>
            <ul className="space-y-1 rounded-2xl border hairline bg-charcoal/30 p-2">
              {(Object.keys(CATEGORY_LABELS) as NotificationCategory[]).map((cat) => (
                <li key={cat}>
                  <Toggle
                    label={CATEGORY_LABELS[cat].title}
                    description={CATEGORY_LABELS[cat].description}
                    checked={Boolean(prefs.categories[cat]) && prefs.master}
                    disabled={!prefs.master}
                    onChange={(v) => setCategory(cat, v)}
                  />
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-8">
            <h2 className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-muted-foreground">
              <Moon size={12} /> Quiet Hours
            </h2>
            <div className="rounded-2xl border hairline bg-charcoal/30 p-2">
              <Toggle
                label="Enable quiet hours"
                description="Suppress notifications during a nightly window."
                checked={prefs.quietHoursEnabled}
                onChange={(v) => commit({ ...prefs, quietHoursEnabled: v })}
              />
              {prefs.quietHoursEnabled && (
                <div className="grid grid-cols-2 gap-3 px-4 pb-4">
                  <TimeField
                    label="From"
                    value={prefs.quietStart}
                    onChange={(v) => commit({ ...prefs, quietStart: v })}
                  />
                  <TimeField
                    label="Until"
                    value={prefs.quietEnd}
                    onChange={(v) => commit({ ...prefs, quietEnd: v })}
                  />
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Timezone: {prefs.timezone}
            </p>
          </section>

          <section className="mt-8">
            <h2 className="mb-3 text-xs uppercase tracking-[0.24em] text-muted-foreground">Digest</h2>
            <div className="rounded-2xl border hairline bg-charcoal/30 p-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                A single, curated transmission — activations, releases, and replies to you — delivered
                on your schedule. Respects quiet hours and your local timezone.
              </p>
              <CadenceField
                label="Email digest"
                value={prefs.digestEmail}
                onChange={(v) => commit({ ...prefs, digestEmail: v })}
              />
              <CadenceField
                label="Push digest"
                value={prefs.digestPush}
                onChange={(v) => commit({ ...prefs, digestPush: v })}
              />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Delivery hour (local)</label>
                <select
                  value={prefs.digestHour}
                  onChange={(e) => commit({ ...prefs, digestHour: Number(e.target.value) })}
                  className="mt-1 w-full rounded-lg border hairline bg-obsidian px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                  ))}
                </select>
              </div>
            </div>
          </section>



          <section className="mt-8">
            <NewsletterPreferences />
          </section>

          {savedAt && (
            <p className="mt-6 text-xs text-gold" aria-live="polite">
              Saved.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Toggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl p-3 ${
        disabled ? "opacity-50" : "hover:bg-obsidian/40"
      }`}
    >
      <span className="min-w-0">
        <span className="block text-sm text-foreground">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>}
      </span>
      <span
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-gold-gradient" : "bg-obsidian border hairline"
        }`}
      >
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          className={`inline-block h-4 w-4 rounded-full bg-obsidian shadow transition-transform ${
            checked ? "translate-x-6 bg-white" : "translate-x-1"
          }`}
        />
      </span>
    </label>
  );
}

function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border hairline bg-obsidian px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none"
      />
    </label>
  );
}

function CadenceField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: "off" | "daily" | "weekly";
  onChange: (v: "off" | "daily" | "weekly") => void;
}) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</label>
      <div className="mt-1 inline-flex rounded-lg border hairline bg-obsidian p-1">
        {(["off", "daily", "weekly"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`min-w-[64px] rounded-md px-3 py-1.5 text-xs capitalize transition-colors ${
              value === opt ? "bg-gold-gradient text-obsidian" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}
